import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useSnackbar } from "notistack";
import { sortTasksByDue } from "./sortTasksByDue";
import { entityTaskQueryKey, isTasksQueryKey } from "./taskQuery";
import type { CreateTaskRequest, TaskEntityType, TaskResponse, UpdateTaskRequest } from "./tasksApi";
import { completeTask as completeTaskApi, createTask, deleteTask, getTasksForEntity, updateTask } from "./tasksApi";

export const useEntityTasks = (entityType: TaskEntityType, entityId: string, customerId: string) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { data, isLoading } = useQuery({
    queryKey: entityTaskQueryKey(entityType, entityId),
    queryFn: ({ signal }) => getTasksForEntity(entityType, entityId, signal),
  });

  const createTaskMutation = useMutation<TaskResponse, unknown, { request: Omit<CreateTaskRequest, "customerId"> }>({
    mutationFn: ({ request }) => createTask(entityType, entityId, customerId, request),
    onSuccess: async () => {
      enqueueSnackbar("Zadanie zostało utworzone", { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: entityTaskQueryKey(entityType, entityId) });
      await queryClient.invalidateQueries({ predicate: isTasksQueryKey });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się utworzyć zadania. Powód: ${(error as Error).message}`, { variant: "error" });
    },
  });

  const deleteTaskMutation = useMutation<void, unknown, { taskId: string }>({
    mutationFn: ({ taskId }) => deleteTask(entityType, entityId, taskId),
    onSuccess: async () => {
      enqueueSnackbar("Zadanie zostało usunięte", { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: entityTaskQueryKey(entityType, entityId) });
      await queryClient.invalidateQueries({ predicate: isTasksQueryKey });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się usunąć zadania. Powód: ${(error as Error).message}`, { variant: "error" });
    },
  });

  const completeTaskMutation = useMutation<TaskResponse, unknown, { taskId: string; note?: string }>({
    mutationFn: ({ taskId, note }) => completeTaskApi(entityType, entityId, taskId, note),
    onSuccess: async () => {
      enqueueSnackbar("Zadanie oznaczone jako wykonane", { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: entityTaskQueryKey(entityType, entityId) });
      await queryClient.invalidateQueries({ predicate: isTasksQueryKey });
    },
    onError: (error) => {
      const status = isAxiosError(error) ? error?.response?.status : undefined;
      if (status === 409) {
        enqueueSnackbar("To zadanie jest już oznaczone jako wykonane", { variant: "warning" });
      } else {
        enqueueSnackbar(`Nie udało się oznaczyć zadania. Powód: ${(error as Error).message}`, { variant: "error" });
      }
    },
  });

  const updateTaskMutation = useMutation<TaskResponse, unknown, { taskId: string; request: UpdateTaskRequest }>({
    mutationFn: ({ taskId, request }) => updateTask(entityType, entityId, taskId, request),
    onSuccess: async () => {
      enqueueSnackbar("Zadanie zostało zaktualizowane", { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: entityTaskQueryKey(entityType, entityId) });
      await queryClient.invalidateQueries({ predicate: isTasksQueryKey });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaktualizować zadania. Powód: ${(error as Error).message}`, { variant: "error" });
    },
  });

  const sortedTasks = sortTasksByDue(data);

  return {
    tasks: sortedTasks,
    isLoading,
    createTask: (request: Omit<CreateTaskRequest, "customerId">) => createTaskMutation.mutateAsync({ request }),
    deleteTask: (taskId: string) => deleteTaskMutation.mutateAsync({ taskId }),
    completeTask: (taskId: string, note?: string) => completeTaskMutation.mutateAsync({ taskId, note }),
    updateTask: (taskId: string, request: UpdateTaskRequest) => updateTaskMutation.mutateAsync({ taskId, request }),
  };
};
